import React from 'react';
import { NavLink } from 'react-router-dom';
import './UnitedNations.css';
import './AboutPages.css';

const OUR_APPROACH_VALUES = [
  {
    name: 'COMPASSION',
    icon: '❤️',
    desc: 'Placing human dignity, empathy, and the well-being of communities at the heart of our engagement.'
  },
  {
    name: 'COLLABORATION',
    icon: '🤝',
    desc: 'Recognizing that meaningful progress is strengthened through partnership, dialogue, and collective endeavour.'
  },
  {
    name: 'EMPOWERMENT',
    icon: '⚡',
    desc: 'Creating pathways through which individuals and communities can develop knowledge, confidence, capability, and opportunity.'
  },
  {
    name: 'SUSTAINABILITY',
    icon: '🌱',
    desc: 'Pursuing solutions that create enduring value and remain meaningful for generations to come.'
  },
  {
    name: 'INTEGRITY',
    icon: '🛡️',
    desc: 'Upholding professionalism, ethical conduct, accountability, responsibility, and respect in all areas of our work.'
  }
];

function AboutStrategicPillars({ siteData }) {
  return (
    <div className="page-shell about-subpage">
      {/* Hero Banner */}
      <div className="about-hero">
        <span className="about-hero-pill">Strategic Framework</span>
        <h1 className="about-hero-title">Strategic Pillars & SDG Commitment</h1>
        <p className="about-hero-tagline">Turning Global Aspirations into Actionable & Enduring Social Value</p>
        <p className="about-hero-desc">
          UNGDO’s strategic approach bridges policy frameworks with grassroots community engagement. Through education, humanitarian response, youth empowerment, and ethical partnerships, we build sustainable resilience.
        </p>
      </div>

      {/* Navigation Pills */}
      <div className="about-nav-pills">
        <NavLink to="/about" className="about-pill-btn">Overview & Mission</NavLink>
        <NavLink to="/about/areas-of-focus" className="about-pill-btn">12 Areas of Focus</NavLink>
        <NavLink to="/about/strategic-pillars" className="about-pill-btn active">Strategic Pillars & SDGs</NavLink>
        <NavLink to="/about/governance" className="about-pill-btn">Governance & Leadership</NavLink>
      </div>

      {/* 1. Our Commitment to the SDGs */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">🌐</div>
          <div className="pillar-title-group">
            <h3>Our Commitment to the Sustainable Development Goals</h3>
            <span className="pillar-subtitle">Translating Agenda 2030 into Practical Engagement</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            The United Nations Sustainable Development Goals (SDGs) represent an internationally recognized framework for addressing some of the most pressing social, economic, and environmental challenges of our time.
          </p>
          <p>
            UNGDO is committed to promoting greater awareness and understanding of these global development priorities and to encouraging their translation into meaningful action at community and institutional levels.
          </p>
          <p>
            We believe that the SDGs should not remain confined to policy documents or institutional frameworks. Their enduring value lies in their ability to inspire practical engagement and collective responsibility. Every individual, community, institution, organization, and sector has a role to play in advancing sustainable development.
          </p>
          <p>
            UNGDO seeks to contribute to this collective endeavour by fostering awareness, encouraging collaboration, supporting development-oriented initiatives, and creating platforms through which individuals and communities can engage with the broader vision of sustainable progress.
          </p>
        </div>
      </div>

      {/* 2. Education, Awareness & Capacity Building */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">📘</div>
          <div className="pillar-title-group">
            <h3>Education, Awareness & Capacity Building</h3>
            <span className="pillar-subtitle">Knowledge as a Catalyst for Transformation</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            Education is among the most powerful instruments of sustainable development. UNGDO recognizes that knowledge has the ability to transform not only individual lives but entire communities and generations.
          </p>
          <p>
            Through educational outreach, awareness initiatives, knowledge sharing, leadership development, and capacity-building programmes, we seek to encourage informed participation and cultivate individuals capable of responding thoughtfully and constructively to the challenges of a rapidly changing world.
          </p>
          <p>
            <strong>Our philosophy is simple:</strong> education should not merely impart knowledge; it should inspire responsibility, cultivate leadership, expand opportunity, and empower action.
          </p>
        </div>
      </div>

      {/* 3. Humanitarian Engagement & Community Development */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">🕊️</div>
          <div className="pillar-title-group">
            <h3>Humanitarian Engagement & Community Development</h3>
            <span className="pillar-subtitle">Respecting Human Dignity & Fostering Resilience</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            At the heart of meaningful development lies a profound respect for human dignity. UNGDO seeks to encourage humanitarian and community-oriented initiatives that respond to social needs, strengthen resilience, expand opportunity, and contribute to the well-being of individuals and communities.
          </p>
          <p>
            Our approach emphasizes dignity, empowerment, participation, and sustainability. Wherever possible, our objective is not merely to address immediate needs but to encourage pathways towards greater resilience, capacity, self-reliance, and long-term community advancement.
          </p>
        </div>
      </div>

      {/* 4. Partnerships for Progress */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">🤝</div>
          <div className="pillar-title-group">
            <h3>Partnerships for Progress</h3>
            <span className="pillar-subtitle">Collaboration Creates Possibility. Collective Action Creates Impact.</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            The scale and complexity of contemporary global challenges demand cooperation beyond institutional and geographical boundaries. UNGDO, therefore, seeks to cultivate meaningful relationships with professionals, institutions, educators, community organizations, businesses, civil society, development partners, and individuals who share a commitment to constructive social progress.
          </p>
          <p>
            We value partnerships founded upon mutual respect, shared purpose, professional integrity, knowledge exchange, and responsible collaboration. By connecting expertise, experience, resources, and opportunity, we seek to create an environment in which ideas can evolve into initiatives and initiatives that can contribute to lasting impact.
          </p>
        </div>
      </div>

      {/* 5. Youth & Future Leadership */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">🌟</div>
          <div className="pillar-title-group">
            <h3>Youth & Future Leadership</h3>
            <span className="pillar-subtitle">Investing in the Leaders of Tomorrow</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            Young people are among the most powerful catalysts for innovation, social transformation, and sustainable development. UNGDO seeks to encourage youth participation, leadership, education, professional development, and community engagement.
          </p>
          <p>
            We believe young people should not be regarded solely as beneficiaries of development. They are partners in progress, innovators of change, and emerging leaders whose vision and contribution will shape the future. Empowering young people today is an investment in the resilience and prosperity of tomorrow.
          </p>
        </div>
      </div>

      {/* 6. Women & Inclusive Development */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">⚖️</div>
          <div className="pillar-title-group">
            <h3>Women & Inclusive Development</h3>
            <span className="pillar-subtitle">Recognizing & Empowering Talent & Potential</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            Inclusive development requires that every individual has the opportunity to participate, contribute, and fulfil their potential. UNGDO supports initiatives that encourage education, leadership, equality of opportunity, participation, and empowerment while recognizing the important contribution of women to families, communities, institutions, economies, and societies.
          </p>
          <p>
            We believe that societies achieve greater resilience and prosperity when talent and potential are recognized, encouraged, and given the opportunity to flourish.
          </p>
        </div>
      </div>

      {/* 7. Environmental Responsibility */}
      <div className="pillar-section">
        <div className="pillar-header">
          <div className="pillar-icon-wrap">🌿</div>
          <div className="pillar-title-group">
            <h3>Environmental Responsibility</h3>
            <span className="pillar-subtitle">Protecting the Foundations of Future Generations</span>
          </div>
        </div>
        <div className="pillar-body">
          <p>
            Human prosperity and environmental sustainability are inseparable. UNGDO promotes awareness of environmental stewardship, climate responsibility, sustainable communities, responsible resource utilization, and the preservation of natural ecosystems.
          </p>
          <p>
            We recognize that environmental challenges are not solely ecological concerns; they have profound implications for human health, livelihoods, food security, economic stability, and the future well-being of communities. Sustainable development, therefore, requires a responsible relationship between humanity and the natural world upon which we depend.
          </p>
        </div>
      </div>

      {/* 8. Our Approach (5 Values) */}
      <div style={{ marginTop: '48px', marginBottom: '32px' }}>
        <div className="about-section-header" style={{ textAlign: 'center' }}>
          <span className="about-section-subtitle">Operational Excellence</span>
          <h2 className="about-section-title">OUR APPROACH</h2>
          <p className="about-section-lead" style={{ maxWidth: '700px', margin: '0 auto' }}>
            Our work is anchored in five enduring principles that guide every programme, partnership, and institutional initiative worldwide.
          </p>
        </div>

        <div className="approach-grid">
          {OUR_APPROACH_VALUES.map((val) => (
            <div key={val.name} className="approach-card">
              <div className="approach-icon">{val.icon}</div>
              <h4>{val.name}</h4>
              <p>{val.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div className="about-cta-banner">
        <h2 className="about-cta-title">Explore Interactive SDG Tools</h2>
        <p className="about-cta-text">
          Engage directly with the 17 UN Sustainable Development Goals through our interactive explorer tool and academic resources.
        </p>
        <NavLink to="/united-nations/sdg-interactive" className="about-cta-btn">
          Launch SDG Interactive Explorer →
        </NavLink>
      </div>
    </div>
  );
}

export default AboutStrategicPillars;
